import React, { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import confetti from "canvas-confetti";
import api from "../container/helper/Api";
import Toastify from "../container/helper/Toastify";
import { getProfile } from "../container/redux/userSlice";
import kingsterLogo from "../logo.png";
import masterclassThumbnail from "../image/masterclassImg.png";
const OTP_LENGTH = 6;

const CONFETTI_COLORS = ["#FF3600", "#1D83F8", "#FFD700", "#22c55e", "#BD2800"];

const fireCardConfetti = (canvas) => {
  if (!canvas) return;

  const shoot = confetti.create(canvas, {
    resize: true,
    useWorker: false,
  });

  const duration = 2000;
  const end = Date.now() + duration;

  shoot({
    particleCount: 50,
    spread: 60,
    origin: { y: 0.45 },
    colors: CONFETTI_COLORS,
  });

  const frame = () => {
    shoot({
      particleCount: 2,
      angle: 60,
      spread: 45,
      origin: { x: 0, y: 0.55 },
      colors: CONFETTI_COLORS,
    });
    shoot({
      particleCount: 2,
      angle: 120,
      spread: 45,
      origin: { x: 1, y: 0.55 },
      colors: CONFETTI_COLORS,
    });
    if (Date.now() < end) {
      requestAnimationFrame(frame);
    }
  };
  frame();
};

const fieldInputClass =
  "w-full rounded-lg border border-solid border-[#D1D5DB] bg-white px-4 py-3 text-gray-900 outline-none focus:border-[#FF3600] focus:ring-1 focus:ring-[#FF3600]";

const otpBoxClass =
  "h-12 w-11 rounded-lg border border-solid border-[#D1D5DB] bg-white text-center text-lg font-semibold text-gray-900 outline-none focus:border-[#FF3600] focus:ring-1 focus:ring-[#FF3600]";

const gradientButtonClass =
  "h-[58px] w-full rounded-full text-base font-bold uppercase text-white disabled:opacity-70";

const gradientButtonStyle = {
  background: "linear-gradient(90deg, #FF3600 0%, #BD2800 100%)",
};

const MasterclassAuthModal = ({
  isOpen,
  onClose,
  onSuccess,
  courseId,
  courseTitle,
  source = "Website",
  durationLabel = "20 Mins",
}) => {
  const dispatch = useDispatch();
  const otpRefs = useRef([]);
  const confettiCanvasRef = useRef(null);
  const [step, setStep] = useState("mobile");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [registrationData, setRegistrationData] = useState({
    name: "",
    email: "",
    location: "",
  });
  const [isNewUserFlow, setIsNewUserFlow] = useState(false);
  const [successType, setSuccessType] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setStep("mobile");
      setPhone("");
      setOtpDigits(Array(OTP_LENGTH).fill(""));
      setRegistrationData({ name: "", email: "", location: "" });
      setIsNewUserFlow(false);
      setSuccessType(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (step !== "success") return;

    const raf = requestAnimationFrame(() => {
      if (confettiCanvasRef.current) {
        fireCardConfetti(confettiCanvasRef.current);
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [step]);

  const completeAuth = async (token, isNewUser) => {
    localStorage.setItem("token", token);
    await dispatch(getProfile());
    setSuccessType(isNewUser ? "register" : "login");
    setStep("success");
    setTimeout(() => {
      onSuccess();
    }, 1800);
  };

  const handleGetOtp = async () => {
    if (!/^\d{10}$/.test(phone)) {
      Toastify.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setLoading(true);
    try {
      const { data, status } = await api.initMasterclassAuth({ phone });
      if (status === 200) {
        if (data.data.userExists) {
          setIsNewUserFlow(false);
          setStep("otp");
          Toastify.success("OTP sent to your mobile number");
        } else {
          setIsNewUserFlow(true);
          setStep("register");
        }
      }
    } catch (error) {
      Toastify.error(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSendOtp = async (e) => {
    e.preventDefault();
    const { name, email, location } = registrationData;
    if (!name.trim() || !email.trim()) {
      Toastify.error("Please fill all fields");
      return;
    }
    if (!location.trim()) {
      Toastify.error("Please enter your location (City / State / Country)");
      return;
    }

    if (!/^\d{10}$/.test(phone)) {
      Toastify.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setLoading(true);
    try {
      const { status } = await api.registerMasterclassStudent({
        name: name.trim(),
        email: email.trim(),
        phone,
        location: location.trim(),
      });
      if (status === 200) {
        setStep("register-otp");
        setOtpDigits(Array(OTP_LENGTH).fill(""));
        Toastify.success("OTP sent to your mobile number");
      }
    } catch (error) {
      Toastify.error(error.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const getOtpValue = () => otpDigits.join("");

  const trackMetaLead = () => {
    try {
      if (typeof window.fbq === "function") {
        window.fbq("track", "Lead");
      }
    } catch (e) {
      // Pixel may be blocked by ad blockers; never break the auth flow.
    }
  };

  const handleVerifyOtp = async () => {
    const mobileOtp = getOtpValue();
    if (mobileOtp.length !== OTP_LENGTH) {
      Toastify.error("Please enter the 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      if (isNewUserFlow || step === "register-otp") {
        const { data, status } = await api.verifyMasterclassRegister({
          name: registrationData.name.trim(),
          email: registrationData.email.trim(),
          location: registrationData.location.trim(),
          phone,
          mobileOtp,
          courseId,
          source,
        });
        if (status === 200) {
          trackMetaLead();
          await completeAuth(data.data.token, true);
        }
      } else {
        const { data, status } = await api.verifyMasterclassOtp({
          phone,
          mobileOtp,
          courseId,
          source,
        });
        if (status === 200) {
          trackMetaLead();
          await completeAuth(data.data.token, false);
        }
      }
    } catch (error) {
      Toastify.error(error.response?.data?.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const payload =
        isNewUserFlow || step === "register-otp"
          ? { phone, email: registrationData.email, flow: "register" }
          : { phone, flow: "login" };
      const { status } = await api.resendMasterclassOtp(payload);
      if (status === 200) {
        Toastify.success("OTP resent successfully");
      }
    } catch (error) {
      Toastify.error(error.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otpDigits];
    next[index] = value;
    setOtpDigits(next);
    if (value && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleGoBack = () => {
    if (step === "otp") {
      setStep("mobile");
      setOtpDigits(Array(OTP_LENGTH).fill(""));
      return;
    }
    if (step === "register") {
      setStep("mobile");
      setIsNewUserFlow(false);
      return;
    }
    if (step === "register-otp") {
      setStep("register");
      setOtpDigits(Array(OTP_LENGTH).fill(""));
    }
  };

  const showOutsideBack = step === "otp" || step === "register-otp";

  if (!isOpen) return null;

  const showOtpStep = step === "otp" || step === "register-otp";
  const showPreview = step === "mobile";

  const isMobileStep = step === "mobile";

  const renderPreviewBlock = (compact = false) => (
    <div
      className={`w-full px-6 md:px-10 ${compact ? "pt-4 md:pt-5" : "pt-6 md:pt-8"
        }`}
    >
      <div className="relative w-full overflow-hidden rounded-xl bg-[#0a1f44]">
        <img
          src={masterclassThumbnail}
          alt="Masterclass preview"
          className={
            compact
              ? "h-[160px] w-full object-cover md:h-[350px]"
              : "aspect-[16/9] w-full object-cover"
          }
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className={`flex items-center justify-center rounded-full bg-white shadow-md ${compact ? "h-10 w-10" : "h-12 w-12 md:h-14 md:w-14"
              }`}
          >
            <span
              className={`ml-0.5 text-black ${compact ? "text-base" : "text-lg md:text-xl"
                }`}
            >
              ▶
            </span>
          </div>
        </div>
      </div>
      <h3
        className={`text-left font-bold leading-snug text-[#111827] ${compact
          ? "mt-2 text-base md:text-lg"
          : "mt-4 text-lg md:text-[22px]"
          }`}
      >
        Free Masterclass on {courseTitle || "this program"}
      </h3>
      <p
        className={`text-left text-gray-500 ${compact ? "mt-0.5 text-xs md:text-sm" : "mt-1 text-sm md:text-base"
          }`}
      >
        {/* {durationLabel} */}
        20 min
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/70 px-4 py-8">
      <div className="relative w-full max-w-[520px] md:max-w-[780px] lg:max-w-[820px]">
        {showOutsideBack && (
          <button
            type="button"
            onClick={handleGoBack}
            className="absolute -top-10 left-0 z-30 flex h-10 w-10 items-center justify-center bg-transparent p-0 hover:opacity-80"
            aria-label="Go back"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 8 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                d="M7 13L1 7L7 1"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        <button
          type="button"
          onClick={onClose}
          className="absolute -top-10 right-0 z-30 flex h-10 w-10 items-center justify-center bg-transparent p-0 text-3xl font-light leading-none text-white hover:text-white/80"
          aria-label="Close"
        >
          ×
        </button>

        <div className="relative w-full overflow-hidden rounded-2xl border-2 border-[#FF3600] bg-white shadow-2xl min-h-[480px] md:min-h-[520px] flex flex-col">
          {step === "success" && (
            <canvas
              ref={confettiCanvasRef}
              className="pointer-events-none absolute inset-0 z-10 h-full w-full"
              aria-hidden
            />
          )}
          {showPreview && renderPreviewBlock(isMobileStep)}

          <div
            className={`flex flex-col items-center px-6 md:px-10 ${isMobileStep ? "pb-5 pt-4" : "pb-8 pt-6 md:pb-10"
              } flex-1 justify-center`}
          >
            {step === "success" ? (
              <div className="relative z-20 py-8 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500 text-3xl text-white">
                  ✓
                </div>
                <h3 className="text-2xl font-bold text-[#02054C]">
                  {successType === "login"
                    ? "Logged in Successfully"
                    : "Registration Successful"}
                </h3>
                <p className="mt-2 text-gray-500">
                  Redirecting you to the Masterclass
                </p>
              </div>
            ) : step === "register" ? (
              <>
                <button
                  type="button"
                  onClick={handleGoBack}
                  className="absolute left-4 top-4 z-20 flex h-9 w-9 items-center justify-center bg-transparent p-0 hover:opacity-70"
                  aria-label="Go back"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 8 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <path
                      d="M7 13L1 7L7 1"
                      stroke="#1F2937"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                <div className="mb-6 flex justify-center">
                  <img
                    src={kingsterLogo}
                    alt="Kingster"
                    className="h-14 w-auto max-w-[200px] object-contain"
                  />
                </div>
                <h3 className="text-center text-2xl font-bold text-[#02054C]">
                  Join Kingster Education
                </h3>
                <p className="mt-2 text-center text-sm text-gray-500">
                  Sign up to watch your free masterclass
                </p>

                <form onSubmit={handleRegisterSendOtp} className="mt-6 w-full max-w-[340px] space-y-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={registrationData.name}
                      onChange={(e) =>
                        setRegistrationData((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      placeholder="Enter your full name"
                      className={fieldInputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={registrationData.email}
                      onChange={(e) =>
                        setRegistrationData((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      placeholder="Enter your email"
                      className={fieldInputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Mobile Number *
                    </label>
                    <div className="flex overflow-hidden rounded-lg border border-solid border-[#D1D5DB] bg-white">
                      <span className="flex items-center border-r border-[#D1D5DB] bg-gray-50 px-3 text-sm text-gray-600">
                        +91
                      </span>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                          setPhone(val);
                        }}
                        placeholder="Enter your mobile number"
                        className="w-full border-0 bg-white px-4 py-3 outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Location (City / State / Country) *
                    </label>
                    <input
                      type="text"
                      value={registrationData.location}
                      onChange={(e) =>
                        setRegistrationData((prev) => ({
                          ...prev,
                          location: e.target.value,
                        }))
                      }
                      placeholder="e.g. Mumbai, Maharashtra, India"
                      className={fieldInputClass}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-2 h-[58px] w-full rounded-full text-base font-bold uppercase text-white disabled:opacity-70"
                    style={{
                      background:
                        "linear-gradient(90deg, #FF3600 0%, #BD2800 100%)",
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      "Send OTP"
                    )}
                  </button>
                </form>

                {/* Terms and Privacy Policy Notice */}
                <p className="mt-4 text-center text-xs text-gray-500 max-w-[340px]">
                  By continuing, you agree to our{" "}
                  <a
                    href="https://kingster.edu.in/terms-and-conditions/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FF3600] hover:underline font-medium"
                  >
                    Terms of Use
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://kingster.edu.in/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FF3600] hover:underline font-medium"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </>
            ) : showOtpStep ? (
              <>
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#EEF5FF]">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                      stroke="#02054C"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M12 7V12L15 15"
                      stroke="#02054C"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <p className="mb-6 max-w-[320px] text-center text-sm text-gray-500 md:text-base">
                  We have sent a 6-digit OTP to <br></br> +91 {phone}
                </p>

                <div className="flex justify-center gap-2">
                  {otpDigits.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        otpRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className={otpBoxClass}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={loading}
                  className={`mt-6 w-full max-w-[420px] ${gradientButtonClass}`}
                  style={gradientButtonStyle}
                >
                  {loading ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Verify"
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={loading}
                  className="mt-4 max-w-[420px] w-full text-center text-sm font-medium text-[#FF3600] disabled:opacity-60"
                >
                  Resend OTP
                </button>

                <div className="mt-6 flex justify-center gap-2">
                  <span
                    className={`h-2 w-2 rounded-full ${step === "register-otp" ? "bg-blue-500" : "bg-gray-300"
                      }`}
                  />
                  <span className="h-2 w-8 rounded-full bg-blue-500" />
                </div>
              </>
            ) : (
              <>
                <p className="mb-4 max-w-[520px] text-center text-sm font-medium text-[#02054C] md:text-base">
                  Enter your Mobile number to access Free Masterclass
                </p>

                <div className="w-full max-w-[350px]">
                  <div className="flex w-full overflow-hidden rounded-full border border-solid border-[#D1D5DB] bg-white">
                    <span className="flex items-center gap-1 border-r border-[#D1D5DB] bg-gray-50 px-4 text-gray-500">
                      📞
                    </span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setPhone(val);
                      }}
                      placeholder="Enter your Mobile Number"
                      className="w-full bg-white px-4 py-3 outline-none"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGetOtp}
                  disabled={loading}
                  className="mt-4 h-[52px] w-full max-w-[350px] rounded-full text-base font-bold uppercase text-white disabled:opacity-70"
                  style={gradientButtonStyle}
                >
                  {loading ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Get OTP"
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setIsNewUserFlow(true);
                    setStep("register");
                  }}
                  className="mt-5 text-sm font-medium text-gray-600 hover:text-[#FF3600]"
                >
                  Sign up
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterclassAuthModal;